import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';

import { Checkbox } from '@atlaskit/checkbox';
import { cssMap } from '@atlaskit/css';
import FolderClosedIcon from '@atlaskit/icon/core/folder-closed';
import PageIcon from '@atlaskit/icon/core/page';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { RawAction } from '../__types__/SpaceSuggestion.ts';

type UnknownRawAction = {
	actionType: string;
	parameters?: unknown;
};

export type SuggestionReviewRowProps = {
	action: RawAction | UnknownRawAction;
	index: number;
	isChecked: boolean;
	onCheckedChange: (index: number, isChecked: boolean) => void;
};

type RowContent = {
	actionType: string;
	actionLabel: string;
	title: string;
	subtext: string;
	contentType: 'folder' | 'page';
	checkboxLabel: string;
};

export function SuggestionReviewRow(props: SuggestionReviewRowProps): React.JSX.Element {
	const { action, index, isChecked, onCheckedChange } = props;
	const { formatMessage } = useIntl();
	const rowContent = getActionRowContent(action, formatMessage);
	const ContentIcon = rowContent.contentType === 'folder' ? FolderClosedIcon : PageIcon;
	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			onCheckedChange(index, event.currentTarget.checked);
		},
		[index, onCheckedChange],
	);

	return (
		<div css={styles.row}>
			<Checkbox
				xcss={styles.checkbox}
				isChecked={isChecked}
				onChange={handleChange}
				name={`suggestion-review-row-${rowContent.actionType}-${index}`}
				aria-label={rowContent.checkboxLabel}
				label={
					<Stack space="space.025">
						<div css={styles.labelContent}>
							<div css={styles.actionText}>
								<Text>{rowContent.actionLabel}</Text>
							</div>
							<Box xcss={styles.contentIcon}>
								<ContentIcon label="" size="small" color="currentColor" />
							</Box>
							<div css={styles.titleText}>
								<Text weight="medium">{rowContent.title}</Text>
							</div>
						</div>
						<Text size="small" color="color.text.subtlest">
							{rowContent.subtext}
						</Text>
					</Stack>
				}
			/>
		</div>
	);
}

function getActionRowContent(
	action: RawAction | UnknownRawAction,
	formatMessage: IntlShape['formatMessage'],
): RowContent {
	switch (action.actionType) {
		case 'CREATE_FOLDER': {
			const { parentContentId, parentContentName, title } = (
				action as Extract<RawAction, { actionType: 'CREATE_FOLDER' }>
			).parameters;
			const parentLabel = parentContentName || parentContentId;
			return {
				actionType: action.actionType,
				actionLabel: formatMessage(actionLabelMessages.CREATE_FOLDER),
				title,
				subtext: formatMessage(i18n.createFolderSubtext, {
					parentContentName: parentLabel,
				}),
				contentType: 'folder',
				checkboxLabel: formatMessage(i18n.checkboxLabel, {
					action: formatMessage(actionLabelMessages.CREATE_FOLDER),
					title,
				}),
			};
		}
		case 'MOVE': {
			const { contentId, contentName, destinationAlias, destinationName } = (
				action as Extract<RawAction, { actionType: 'MOVE' }>
			).parameters;
			const title = contentName || contentId;
			const destinationLabel = destinationName || destinationAlias;
			return {
				actionType: action.actionType,
				actionLabel: formatMessage(actionLabelMessages.MOVE),
				title,
				subtext: destinationLabel
					? formatMessage(i18n.moveSubtext, { destinationName: destinationLabel })
					: '',
				contentType: 'page',
				checkboxLabel: formatMessage(i18n.checkboxLabel, {
					action: formatMessage(actionLabelMessages.MOVE),
					title,
				}),
			};
		}
		case 'RENAME': {
			const { contentId, contentName, title } = (
				action as Extract<RawAction, { actionType: 'RENAME' }>
			).parameters;
			const previousTitle = contentName || contentId;
			return {
				actionType: action.actionType,
				actionLabel: formatMessage(actionLabelMessages.RENAME),
				title,
				subtext: previousTitle,
				contentType: 'page',
				checkboxLabel: formatMessage(i18n.checkboxLabel, {
					action: formatMessage(actionLabelMessages.RENAME),
					title,
				}),
			};
		}
		default: {
			return {
				actionType: action.actionType,
				actionLabel: formatMessage(i18n.unknownActionLabel),
				title: action.actionType,
				subtext: formatMessage(i18n.unknownActionSubtext, {
					parameters: stringifyUnknownParameters(action.parameters),
				}),
				contentType: 'page',
				checkboxLabel: formatMessage(i18n.checkboxLabel, {
					action: formatMessage(i18n.unknownActionLabel),
					title: action.actionType,
				}),
			};
		}
	}
}

function stringifyUnknownParameters(parameters: unknown): string {
	if (parameters === undefined) {
		return '';
	}

	try {
		return JSON.stringify(parameters);
	} catch {
		return String(parameters);
	}
}

const styles = cssMap({
	row: {
		paddingBlock: token('space.100'),
		paddingInline: token('space.100'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.large'),
		backgroundColor: token('elevation.surface'),
		cursor: 'pointer',
	},
	checkbox: {
		alignItems: 'flex-start',
	},
	labelContent: {
		display: 'grid',
		gridTemplateColumns: 'max-content max-content minmax(0, 1fr)',
		columnGap: token('space.050'),
		alignItems: 'center',
		minWidth: 0,
	},
	actionText: {
		whiteSpace: 'nowrap',
	},
	contentIcon: {
		color: token('color.text.discovery'),
	},
	titleText: {
		minWidth: 0,
		overflowWrap: 'break-word',
	},
});

const actionLabelMessages = defineMessages({
	CREATE_FOLDER: {
		id: 'space-suggestions.review-row.action.create-folder',
		defaultMessage: 'Create',
		description: 'Action label for a review row that creates a folder',
	},
	MOVE: {
		id: 'space-suggestions.review-row.action.move',
		defaultMessage: 'Move',
		description: 'Action label for a review row that moves content',
	},
	RENAME: {
		id: 'space-suggestions.review-row.action.rename',
		defaultMessage: 'Rename',
		description: 'Action label for a review row that renames content',
	},
});

const i18n = defineMessages({
	createFolderSubtext: {
		id: 'space-suggestions.review-row.create-folder-subtext',
		defaultMessage: 'in {parentContentName}',
		description: 'Details shown under a create-folder action in the suggestion review panel',
	},
	moveSubtext: {
		id: 'space-suggestions.review-row.move-subtext',
		defaultMessage: 'to {destinationName}',
		description: 'Details shown under a move action in the suggestion review panel',
	},
	unknownActionLabel: {
		id: 'space-suggestions.review-row.unknown-action-label',
		defaultMessage: 'Unknown action',
		description: 'Fallback action label when an unexpected action type is returned by the API',
	},
	unknownActionSubtext: {
		id: 'space-suggestions.review-row.unknown-action-subtext',
		defaultMessage: 'parameters: {parameters}',
		description: 'Fallback raw API details when an unexpected action type is returned by the API',
	},
	checkboxLabel: {
		id: 'space-suggestions.review-row.checkbox-label',
		defaultMessage: '{action} {title}',
		description: 'Accessible label for selecting or deselecting a suggestion review row',
	},
});
